import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Shirt, Upload, Check, Building2, AlertCircle, Linkedin, Phone, ArrowRight, LogOut } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const [step, setStep] = useState(1);
  const [role, setRole] = useState('');
  const [saving, setSaving] = useState(false);
  const [showEmailWarning, setShowEmailWarning] = useState(false);

  const PERSONAL_DOMAINS = [
    'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.co.in', 'yahoo.in',
    'hotmail.com', 'outlook.com', 'live.com', 'msn.com',
    'aol.com', 'icloud.com', 'me.com', 'mac.com',
    'protonmail.com', 'proton.me', 'zoho.com', 'mail.com',
    'ymail.com', 'rediffmail.com', 'rocketmail.com',
  ];

  const userEmail = user?.email || '';
  const emailDomain = userEmail.includes('@') ? userEmail.split('@')[1].toLowerCase() : '';
  const isPersonalEmail = PERSONAL_DOMAINS.includes(emailDomain);

  const handleStartupClick = () => {
    if (isPersonalEmail) {
      setShowEmailWarning(true);
    } else {
      setRole('startup');
      setStep(2);
    }
  };

  const handleSignOutAndSwitch = async () => {
    await logout();
    navigate('/login');
  };

  // Startup fields
  const [startupName, setStartupName] = useState('');
  const [tagline, setTagline] = useState('');
  const [website, setWebsite] = useState('');

  // Wearer fields
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [twitterHandle, setTwitterHandle] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [ycScreenshot, setYcScreenshot] = useState(null);
  const [ycPreview, setYcPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState('');
  const fileInputRef = useRef(null);
  const [sampleExpanded, setSampleExpanded] = useState(false);

  // Close lightbox on Escape key
  const handleEscKey = useCallback((e) => {
    if (e.key === 'Escape') setSampleExpanded(false);
  }, []);
  useEffect(() => {
    if (sampleExpanded) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [sampleExpanded, handleEscKey]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please select an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('File must be under 10MB'); return; }

    setYcScreenshot(file);
    setYcPreview(URL.createObjectURL(file));

    // Upload immediately
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/yc-screenshot`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadedUrl(res.data.url);
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed. Please try again.');
      setYcScreenshot(null);
      setYcPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      if (role === 'startup') {
        await axios.post(`${API}/onboard/startup`, {
          startup_name: startupName,
          tagline,
          website,
        }, { withCredentials: true });
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(me.data);
        navigate('/dashboard/startup');
      } else {
        await axios.post(`${API}/onboard/wearer`, {
          name: user?.name,
          linkedin_url: linkedinUrl,
          twitter_handle: twitterHandle,
          phone_number: phoneNumber,
          yc_screenshot_url: uploadedUrl,
        }, { withCredentials: true });
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(me.data);
        navigate('/my-tshirt');
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full bg-white border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-xl p-3 font-medium text-sm focus:outline-none focus:-translate-y-[1px] focus:shadow-[4px_4px_0px_0px_#121212] transition-all";

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F6F1E8' }}>
      <div className="w-full max-w-lg">
        <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[8px_8px_0px_0px_#121212] rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-[#121212] px-8 py-5 text-center">
            <h1 className="text-2xl font-black uppercase tracking-tighter text-[#F6F1E8]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Welcome to The SurfaceArea Project
            </h1>
            <p className="text-sm font-medium text-[#888] mt-1">
              {step === 1 ? 'Who are you?' : role === 'startup' ? 'Set up your startup' : 'Verify your YC acceptance'}
            </p>
          </div>

          <div className="px-8 py-8">
            {/* Step 1: Role Selection */}
            {step === 1 && (
              <div className="space-y-4">
                <button
                  data-testid="role-wearer"
                  onClick={() => { setRole('wearer'); setStep(2); }}
                  className="w-full bg-[#FF6B6B] text-[#121212] font-black uppercase border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-5 flex items-center gap-4"
                >
                  <Shirt className="w-8 h-8" strokeWidth={2.5} />
                  <div className="text-left">
                    <div className="text-lg">I'm a Student</div>
                    <div className="text-xs font-medium normal-case" style={{ color: '#3a1010' }}>I want to wear startup tees & get paid</div>
                  </div>
                </button>
                <button
                  data-testid="role-startup"
                  onClick={handleStartupClick}
                  className="w-full bg-[#E8C86A] text-[#121212] font-black uppercase border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-5 flex items-center gap-4"
                >
                  <Building2 className="w-8 h-8" strokeWidth={2.5} />
                  <div className="text-left">
                    <div className="text-lg">I'm a Startup</div>
                    <div className="text-xs font-medium normal-case" style={{ color: '#555' }}>I want people to wear my brand</div>
                  </div>
                </button>

                {/* Personal email warning */}
                {showEmailWarning && (
                  <div className="bg-[#FF6B6B]/15 border-[3px] border-[#FF6B6B] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-5 animate-fade-in-up" data-testid="personal-email-warning">
                    <div className="flex items-start gap-3 mb-3">
                      <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" strokeWidth={2.5} style={{ color: '#FF6B6B' }} />
                      <div>
                        <div className="font-black uppercase text-sm mb-1" style={{ color: '#FF6B6B' }}>Company Email Required</div>
                        <p className="text-sm font-medium" style={{ color: '#555' }}>
                          You're signed in as <strong>{userEmail}</strong>. Startup accounts require a company email (e.g. <strong>you@yourstartup.com</strong>).
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleSignOutAndSwitch}
                      className="w-full bg-[#FF6B6B] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] hover:-translate-y-[1px] hover:shadow-[4px_4px_0px_0px_#121212] active:translate-y-[3px] active:shadow-none transition-all rounded-xl px-4 py-2.5 flex items-center justify-center gap-2"
                      data-testid="signout-switch-email-btn"
                    >
                      <LogOut className="w-4 h-4" strokeWidth={3} /> Sign Out & Use Company Email
                    </button>
                  </div>
                )}
              </div>
            )}


            {/* Step 2: Wearer — YC Screenshot Upload */}
            {step === 2 && role === 'wearer' && (
              <div className="space-y-5">
                <div className="bg-[#121212] border-[3px] border-[#121212] rounded-xl overflow-hidden">
                  <div className="px-4 py-3">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" strokeWidth={2.5} style={{ color: '#FF6B6B' }} />
                      <p className="text-sm font-black uppercase tracking-wide text-[#F6F1E8]">YC Startup School Verification</p>
                    </div>
                    <p className="text-xs font-medium mb-3" style={{ color: '#aaa' }}>
                      Upload a screenshot of your YC Startup School acceptance email.
                    </p>
                    <div className="bg-[#FF6B6B] rounded-lg px-3 py-2 mb-3">
                      <p className="text-xs font-black text-[#121212]">
                        ⚠️ The <span className="underline">FROM</span> and <span className="underline">TO</span> email addresses MUST be visible in your screenshot.
                      </p>
                    </div>
                    <p className="text-[11px] font-medium" style={{ color: '#888' }}>
                      Our team will review and approve your account within 24 hours.
                    </p>
                  </div>
                  {/* Sample screenshot */}
                  <div className="border-t border-[#333] px-4 py-3">
                    <p className="text-[11px] font-black uppercase tracking-wider mb-2" style={{ color: '#888' }}>Example — what your screenshot should look like:</p>
                    <img
                      src="/assets/yc-review.png"
                      alt="Example YC acceptance email showing from and to addresses"
                      className="w-full rounded-lg border-[2px] border-[#333] cursor-pointer hover:opacity-80 transition-opacity"
                      style={{ maxHeight: '400px', objectFit: 'contain' }}
                      onClick={() => setSampleExpanded(true)}
                      title="Click to enlarge"
                    />
                    <p className="text-[10px] font-medium text-center mt-1.5" style={{ color: '#666' }}>Click image to enlarge</p>
                  </div>
                </div>

                {/* Social links */}
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider mb-2">LinkedIn Profile <span style={{ color: '#FF6B6B' }}>*</span></label>
                  <div className="relative">
                    <Linkedin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" strokeWidth={2.5} style={{ color: '#888' }} />
                    <input
                      className={inputClass}
                      style={{ paddingLeft: '2.5rem' }}
                      placeholder="https://linkedin.com/in/your-profile"
                      value={linkedinUrl}
                      onChange={e => setLinkedinUrl(e.target.value)}
                      data-testid="linkedin-input"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider mb-2">Twitter / X Handle <span className="text-xs font-medium normal-case tracking-normal" style={{ color: '#888' }}>(optional)</span></label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-bold text-sm" style={{ color: '#888' }}>@</span>
                    <input
                      className={inputClass}
                      style={{ paddingLeft: '2.2rem' }}
                      placeholder="yourhandle"
                      value={twitterHandle}
                      onChange={e => setTwitterHandle(e.target.value)}
                      data-testid="twitter-input"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider mb-2">Phone Number <span style={{ color: '#FF6B6B' }}>*</span></label>
                  <div className="flex items-stretch shadow-[3px_3px_0px_0px_#121212] rounded-xl">
                    <div className="flex items-center gap-1.5 bg-[#F6F1E8] border-[3px] border-r-0 border-[#121212] rounded-l-xl px-3 font-bold text-sm select-none" style={{ color: '#555' }}>
                      <Phone className="w-4 h-4 flex-shrink-0" strokeWidth={2.5} style={{ color: '#888' }} />
                      +91
                    </div>
                    <input
                      className="w-full bg-white border-[3px] border-[#121212] rounded-r-xl p-3 font-medium text-sm focus:outline-none transition-all"
                      style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                      placeholder="9876543210"
                      value={phoneNumber}
                      onChange={e => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      inputMode="numeric"
                      maxLength={10}
                      data-testid="phone-input"
                      required
                    />
                  </div>
                </div>

                {/* Upload area */}
                <div
                  className={`border-[3px] border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${ycPreview ? 'border-[#6EE7B7] bg-[#6EE7B7]/5' : 'border-[#121212] hover:border-[#FF6B6B] hover:bg-[#FF6B6B]/5'
                    }`}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="yc-upload-area"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileSelect}
                    data-testid="yc-file-input"
                  />
                  {ycPreview ? (
                    <div>
                      <img src={ycPreview} alt="YC Screenshot" className="max-h-48 mx-auto rounded-lg border-[2px] border-[#121212] mb-3" />
                      {uploading ? (
                        <div className="flex items-center justify-center gap-2 text-sm font-bold">
                          <div className="animate-spin w-4 h-4 border-2 border-[#121212] border-t-transparent rounded-full" />
                          Uploading...
                        </div>
                      ) : uploadedUrl ? (
                        <div className="flex items-center justify-center gap-2 text-sm font-bold" style={{ color: '#16a34a' }}>
                          <Check className="w-4 h-4" strokeWidth={3} /> Uploaded successfully
                        </div>
                      ) : null}
                      <p className="text-xs font-medium mt-2" style={{ color: '#888' }}>Click to replace</p>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-10 h-10 mx-auto mb-3" strokeWidth={1.5} style={{ color: '#aaa' }} />
                      <p className="text-base font-bold mb-1">Upload YC acceptance screenshot</p>
                      <p className="text-xs font-medium" style={{ color: '#888' }}>PNG, JPG, or WEBP up to 10MB</p>
                    </div>
                  )}
                </div>

                <button
                  data-testid="complete-onboarding-btn"
                  onClick={handleComplete}
                  disabled={saving || uploading || !uploadedUrl || !linkedinUrl.trim() || !phoneNumber.trim()}
                  className="w-full bg-[#FF6B6B] text-[#121212] font-black uppercase text-lg border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-4 disabled:opacity-50"
                >
                  {saving ? 'Submitting...' : 'Submit for Review'}
                </button>
                <p className="text-xs font-medium text-center" style={{ color: '#888' }}>
                  Your account will be reviewed within 24 hours
                </p>
              </div>
            )}

            {/* Step 2: Startup */}
            {step === 2 && role === 'startup' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider mb-2">Startup Name *</label>
                  <input
                    data-testid="startup-name-input"
                    className={inputClass}
                    placeholder="Your startup name"
                    value={startupName}
                    onChange={e => setStartupName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider mb-2">Tagline</label>
                  <input
                    className={inputClass}
                    placeholder="One-liner about your startup"
                    value={tagline}
                    onChange={e => setTagline(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider mb-2">Website <span style={{ color: '#FF6B6B' }}>*</span></label>
                  <input
                    className={inputClass}
                    placeholder="https://..."
                    value={website}
                    onChange={e => setWebsite(e.target.value)}
                  />
                </div>
                <button
                  data-testid="complete-onboarding-btn"
                  onClick={handleComplete}
                  disabled={saving || !startupName.trim() || !website.trim()}
                  className="w-full bg-[#E8C86A] text-[#121212] font-black uppercase text-lg border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-4 disabled:opacity-50"
                >
                  {saving ? 'Setting up...' : 'Get Started'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Continue browsing CTA */}
        <div className="text-center mt-6">
          <button
            data-testid="browse-without-signup"
            onClick={() => navigate('/')}
            className="text-sm font-bold hover:text-[#FF6B6B] transition-colors flex items-center gap-2 mx-auto"
          >
            Just browsing? Skip to the market <ArrowRight className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>
      </div>
      {/* Lightbox overlay for sample image */}
      {sampleExpanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.85)', cursor: 'zoom-out' }}
          onClick={() => setSampleExpanded(false)}
          data-testid="sample-lightbox"
        >
          <img
            src="/assets/yc-review.png"
            alt="Example YC acceptance email (enlarged)"
            className="rounded-xl shadow-2xl"
            style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div >
  );
}
